/* ============================================================
   GS Staggered Panels — Golden Star Jewellery
   ONE behaviour file for both panels. Vanilla JS + self-hosted GSAP.

   Modules:
     • GSPanelCore — shared open/close, staggered GSAP timeline (CSS
       fallback), focus trap, scroll lock, scrim, Esc, one-at-a-time registry
     • GSMenu      — navigation panel: views, header triggers, drill-in/back
     • GSCart      — cart drawer: live render, qty/remove, similar upsell,
       reward ladder (server-rendered), free-shipping progress bar,
       add-to-cart routing

   Guards GSAP (window.gsap); if absent OR prefers-reduced-motion, falls back
   to CSS transitions so panels still open/close.
   ============================================================ */
(function () {
  'use strict';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* -------------------- utils -------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function cssEsc(id) { return (window.CSS && CSS.escape) ? CSS.escape(id) : String(id).replace(/"/g, '\\"'); }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeRe(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  /* Dawn's pub/sub. PUB_SUB_EVENTS is a top-level `const` (shared global lexical
     scope, NOT a window property), so read it by name with a typeof guard.
     publish/subscribe are function declarations, so they DO land on window. */
  function PSE(name) { try { return (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS[name] : null; } catch (e) { return null; } }
  function gsPublish(name, data) { try { if (name && typeof publish === 'function') publish(name, data); } catch (e) {} }
  function gsSubscribe(name, cb) { try { if (name && typeof subscribe === 'function') subscribe(name, cb); } catch (e) {} }

  /* Programmatic horizontal scroll via a short, self-terminating rAF tween.
     Native scrollBy({behavior:'smooth'}) is a no-op here — the theme runs a
     smooth-scroll lib that hijacks it — so set scrollLeft directly instead.
     This is a bounded animation (stops at p>=1), not a free-running loop. */
  function smoothScrollTo(el, target) {
    if (!el) return;
    target = Math.max(0, Math.min(target, el.scrollWidth - el.clientWidth));
    var start = el.scrollLeft, dist = target - start;
    if (Math.abs(dist) < 1) return;
    var dur = 340, t0 = null;
    function step(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      el.scrollLeft = start + dist * (1 - Math.pow(1 - p, 3));
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* -------------------- money -------------------- */
  function makeMoney(cfg) {
    var fmt = (cfg && cfg.moneyFormat) || '${{amount}}';
    function withCommas(cents, decimals, thousands, decimal) {
      decimals = decimals == null ? 2 : decimals;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var value = (cents / 100).toFixed(decimals);
      var parts = value.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    function money(cents) {
      cents = Number(cents) || 0;
      try {
        var ph = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!ph) throw 0;
        var val;
        switch (ph[1]) {
          case 'amount': val = withCommas(cents, 2); break;
          case 'amount_no_decimals': val = withCommas(cents, 0); break;
          case 'amount_with_comma_separator': val = withCommas(cents, 2, '.', ','); break;
          case 'amount_with_space_separator': val = withCommas(cents, 2, ' ', ','); break;
          case 'amount_no_decimals_with_comma_separator': val = withCommas(cents, 0, '.', ','); break;
          case 'amount_no_decimals_with_space_separator': val = withCommas(cents, 0, ' '); break;
          case 'amount_with_apostrophe_separator': val = withCommas(cents, 2, "'", '.'); break;
          default: val = withCommas(cents, 2);
        }
        return fmt.replace(/\{\{\s*\w+\s*\}\}/, val);
      } catch (e) {
        try { return (cents / 100).toLocaleString('en-CA', { style: 'currency', currency: (cfg && cfg.currency) || 'CAD' }); }
        catch (e2) { return '$' + (cents / 100).toFixed(2); }
      }
    }
    // Whole-dollar-friendly variant for threshold badges ($300 not $300.00)
    money.clean = function (cents) {
      return money(cents).replace(/([.,])00(?=\D*$)/, '');
    };
    return money;
  }

  /* -------------------- shared scroll lock (iOS-safe, global) -------------------- */
  var savedScrollY = 0;
  var scrollLocked = false;
  function lockScroll() {
    if (scrollLocked) return;
    scrollLocked = true;
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    var b = document.body;
    b.style.position = 'fixed';
    b.style.top = -savedScrollY + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.classList.add('gs-scroll-locked');
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    b.style.width = '';
    b.classList.remove('gs-scroll-locked');
    window.scrollTo(0, savedScrollY);
  }

  /* ============================================================
     GSPanelCore
     ============================================================ */
  var activePanel = null; // one panel open at a time

  function PanelCore(root, hooks) {
    this.root = root;
    this.panel = $('.gs-panel', root);
    this.prelayers = $all('.gs-prelayer', root);
    this.scrim = $('[data-gs-scrim]', root);
    this.position = root.getAttribute('data-gs-position') || 'right';
    this.hooks = hooks || {};
    var cfgEl = $('[data-gs-config]', root);
    try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { this.cfg = {}; }
    this.useGsap = !!window.gsap && !reduced;
    this.isOpen = false;
    this.lastTrigger = null;
    this.openTl = null;
    this._onKeydown = this.onKeydown.bind(this);

    root.classList.add(this.useGsap ? 'gs-js' : 'gs-nogsap');
    if (this.cfg.changeColorOnOpen) root.classList.add('gs-recolor');

    if (this.useGsap) {
      window.gsap.set(this.panel, { xPercent: this.offX() });
      if (this.prelayers.length) window.gsap.set(this.prelayers, { xPercent: this.offX() });
    }
    this.wireCore();
  }
  PanelCore.prototype.offX = function () { return this.position === 'left' ? -102 : 102; };
  PanelCore.prototype.cssNum = function (name, fb) {
    var n = parseFloat(getComputedStyle(this.root).getPropertyValue(name));
    return isNaN(n) ? fb : n;
  };
  PanelCore.prototype.wireCore = function () {
    var self = this;
    var closeBtn = $('[data-gs-close]', this.panel);
    if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });
    if (this.scrim && this.cfg.closeOnClickAway) this.scrim.addEventListener('click', function () { self.close(); });
  };
  PanelCore.prototype.focusable = function () {
    return $all(FOCUSABLE, this.panel).filter(function (el) {
      return el.offsetParent !== null && !el.closest('[hidden]');
    });
  };
  PanelCore.prototype.onKeydown = function (e) {
    if (!this.isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key !== 'Tab') return;
    var f = this.focusable();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  PanelCore.prototype.playOpen = function () {
    if (!this.useGsap) return; // CSS handles it via .gs-open
    var g = window.gsap;
    if (this.openTl) this.openTl.kill();
    g.set(this.panel, { xPercent: this.offX() });
    var tl = g.timeline();
    if (this.prelayers.length) tl.fromTo(this.prelayers, { xPercent: this.offX() },
      { xPercent: 0, duration: 0.5, ease: 'power4.out', stagger: 0.07 }, 0);
    // Clean slide-in: with no prelayers (cart) the panel leads; with prelayers
    // (menu) it trails the gold reveal.
    tl.fromTo(this.panel, { xPercent: this.offX() },
      { xPercent: 0, duration: 0.55, ease: 'power4.out' }, this.prelayers.length ? 0.18 : 0);
    if (this.scrim) tl.fromTo(this.scrim, { opacity: 0 },
      { opacity: this.cssNum('--gs-scrim-opacity', 0.6), duration: 0.5, ease: 'power2.out' }, 0);
    if (this.hooks.onOpenTimeline) this.hooks.onOpenTimeline(tl, this);
    this.openTl = tl;
  };
  PanelCore.prototype.playClose = function (done) {
    if (!this.useGsap) { setTimeout(done, reduced ? 130 : 420); return; }
    var g = window.gsap;
    if (this.openTl) this.openTl.kill();
    var tl = g.timeline({ onComplete: done });
    tl.to(this.panel, { xPercent: this.offX(), duration: 0.32, ease: 'power3.in' }, 0);
    if (this.prelayers.length) tl.to(this.prelayers, { xPercent: this.offX(), duration: 0.32, ease: 'power3.in', stagger: 0.04 }, 0);
    if (this.scrim) tl.to(this.scrim, { opacity: 0, duration: 0.28, ease: 'power2.in' }, 0);
  };
  PanelCore.prototype.open = function (trigger) {
    if (this.isOpen) return;
    if (activePanel && activePanel !== this) activePanel.close(true); // one at a time
    activePanel = this;
    this.isOpen = true;
    this.lastTrigger = trigger || null;
    this.root.classList.add('gs-open');
    this.panel.removeAttribute('inert');
    this.panel.setAttribute('aria-hidden', 'false');
    if (this.scrim) this.scrim.hidden = false;
    if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'true');
    lockScroll();
    if (this.hooks.beforeOpen) this.hooks.beforeOpen(this);
    this.playOpen();
    document.addEventListener('keydown', this._onKeydown);
    var focusEl = $('[data-gs-close]', this.panel) || this.focusable()[0];
    setTimeout(function () { if (focusEl) { try { focusEl.focus(); } catch (e) {} } }, 60);
    if (this.hooks.onOpen) this.hooks.onOpen(this);
  };
  PanelCore.prototype.close = function (silent) {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (activePanel === this) activePanel = null;
    this.root.classList.remove('gs-open');
    document.removeEventListener('keydown', this._onKeydown);
    var self = this;
    this.playClose(function () {
      self.panel.setAttribute('inert', '');
      self.panel.setAttribute('aria-hidden', 'true');
      if (self.scrim) self.scrim.hidden = true;
      if (self.hooks.onClosed) self.hooks.onClosed(self);
    });
    unlockScroll();
    var pid = this.panel.id;
    $all('[aria-controls="' + pid + '"]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    if (this.hooks.onClose) this.hooks.onClose(this);
    if (!silent && this.lastTrigger && this.lastTrigger.focus) { try { this.lastTrigger.focus(); } catch (e) {} }
  };

  /* ============================================================
     GSMenu — navigation panel
     ============================================================ */
  function GSMenu(root) {
    var self = this;
    this.root = root;
    this.currentView = '__root__';
    this.core = new PanelCore(root, {
      onOpenTimeline: function (tl) { self.scheduleItemsAnim(tl); },
      onClosed: function () { self.showView('__root__', false); }
    });
    this.panel = this.core.panel;
    this.showView('__root__', false);
    this.wirePanel();
    this.wireTriggers();
    document.addEventListener('shopify:section:load', function () { self.wireTriggers(); });
  }
  GSMenu.prototype.getView = function (id) { return $('.gs-view[data-gs-view="' + cssEsc(id) + '"]', this.panel); };
  GSMenu.prototype.showView = function (id, replay) {
    var target = this.getView(id) || this.getView('__root__');
    if (!target) return;
    this.currentView = target.getAttribute('data-gs-view');
    $all('.gs-view', this.panel).forEach(function (v) {
      var active = v === target;
      v.hidden = !active;
      if (active) v.setAttribute('data-gs-active', ''); else v.removeAttribute('data-gs-active');
    });
    var back = $('[data-gs-back]', this.panel);
    if (back) back.hidden = (this.currentView === '__root__');
    if (replay && this.core.useGsap) this.animateItems(target);
  };
  GSMenu.prototype.animateItems = function (view) {
    if (!this.core.useGsap || !view) return;
    var g = window.gsap;
    var risers = $all('.gs-view-all, .gs-link, .gs-sublink', view);
    var nums = $all('.gs-num', view);
    g.killTweensOf(risers);
    g.fromTo(risers, { yPercent: 140, rotate: 6, opacity: 0 },
      { yPercent: 0, rotate: 0, opacity: 1, duration: 0.7, ease: 'power4.out', stagger: 0.06, overwrite: true });
    if (nums.length) {
      g.fromTo(nums, { '--sm-num-opacity': 0 },
        { '--sm-num-opacity': 1, duration: 0.5, ease: 'power2.out', stagger: 0.06, delay: 0.12, overwrite: true });
    }
  };
  GSMenu.prototype.scheduleItemsAnim = function (tl) {
    var self = this;
    tl.add(function () { self.animateItems(self.getView(self.currentView)); }, 0.42);
  };
  GSMenu.prototype.switchView = function (id) {
    if (this.currentView === id) return;
    var self = this;
    var outgoing = this.getView(this.currentView);
    if (this.core.useGsap && outgoing) {
      var out = $all('.gs-view-all, .gs-link, .gs-sublink', outgoing);
      window.gsap.killTweensOf(out);
      window.gsap.to(out, { opacity: 0, duration: 0.16, ease: 'power2.in' });
      setTimeout(function () { window.gsap.set(out, { opacity: 1 }); self.showView(id, true); }, 170);
    } else {
      this.showView(id, true);
    }
  };
  GSMenu.prototype.openTo = function (viewId, trigger) {
    viewId = viewId || '__root__';
    if (this.core.isOpen) { this.switchView(viewId); return; }
    this.showView(viewId, false);
    this.core.open(trigger);
  };
  GSMenu.prototype.wirePanel = function () {
    var self = this;
    var back = $('[data-gs-back]', this.panel);
    if (back) back.addEventListener('click', function () { self.switchView('__root__'); });
    this.panel.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-gs-open]');
      if (opener && self.panel.contains(opener)) { e.preventDefault(); self.switchView(opener.getAttribute('data-gs-open')); }
    });
  };
  GSMenu.prototype.wireTriggers = function () {
    var self = this;
    var header = $('.section-header') || document;

    var triggers = $all('[data-gs-menu-trigger]', header);
    if (!triggers.length) triggers = $all('.header__inline-menu summary[id^="HeaderMenu-"]', header);

    triggers.forEach(function (el) {
      var handle = el.getAttribute('data-gs-menu-trigger') || (el.id ? el.id.replace(/^HeaderMenu-/, '') : '');
      if (!handle || !self.getView(handle)) return; // only parents with a pre-rendered view
      if (el.__gsWired) return;
      el.__gsWired = true;
      el.setAttribute('aria-haspopup', 'dialog');
      el.setAttribute('aria-controls', 'gs-menu-panel');
      el.setAttribute('aria-expanded', 'false');
      el.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); self.openTo(handle, el); });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); self.openTo(handle, el); }
      });
    });

    // Mobile hamburger → root view
    var burger = $('header-drawer summary.header__icon--menu, header-drawer > details > summary');
    if (burger && !burger.__gsWired) {
      burger.__gsWired = true;
      burger.setAttribute('aria-haspopup', 'dialog');
      burger.setAttribute('aria-controls', 'gs-menu-panel');
      burger.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); self.openTo('__root__', burger); });
      burger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); self.openTo('__root__', burger); }
      });
    }
  };

  /* ============================================================
     GSCart — cart drawer
     ============================================================ */
  function GSCart(root) {
    var self = this;
    this.root = root;
    this.core = new PanelCore(root, {
      beforeOpen: function () { self.collapseSheet(); },
      onOpen: function () { self.refresh(); },
      onOpenTimeline: function (tl) { self.scheduleAnim(tl); }
    });
    this.panel = this.core.panel;
    this.cfg = this.core.cfg;
    this.money = makeMoney(this.cfg);
    this.cart = null;
    this.upsellBuilt = false;
    this.linesEl = $('[data-gs-cart-lines]', this.panel);
    this.upsellList = $('[data-gs-upsell-list]', this.panel);

    this.wire();
    this.updateShipBadge();
    this.afterRewardRender();
  }

  /* ---- open / triggers ---- */
  GSCart.prototype.openCart = function (trigger) { this.core.open(trigger); };

  /* Clean Mejuri reveal — the panel slide (PanelCore) carries the motion; the
     content just settles in with a soft, upright fade (no gold rotate/stagger). */
  GSCart.prototype.scheduleAnim = function (tl) {
    var self = this;
    tl.add(function () { self.animateLines(); }, 0.24);
  };
  GSCart.prototype.animateLines = function () {
    if (!this.core.useGsap) return;
    var rows = $all('.gs-line', this.linesEl);
    var extras = $all('.gs-shipbar, .gs-order-summary, .gs-upsell', this.panel);
    var targets = rows.concat(extras);
    if (!targets.length) return;
    window.gsap.killTweensOf(targets);
    window.gsap.fromTo(targets, { y: 10, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out', stagger: 0.035, overwrite: true });
  };

  /* ---- data ---- */
  GSCart.prototype.getCart = function () {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  };
  GSCart.prototype.refresh = function () {
    var self = this;
    return this.getCart().then(function (cart) {
      self.cart = cart;
      self.render(cart);
      return cart;
    }).catch(function () {});
  };

  GSCart.prototype.render = function (cart) {
    if (!cart) return;
    // empty state
    this.panel.classList.toggle('is-empty', cart.item_count === 0);
    // count
    $all('[data-gs-cart-count]', this.panel).forEach(function (el) { el.textContent = cart.item_count; });
    // subtotal + order summary rows
    var sub = $('[data-gs-subtotal]', this.panel);
    if (sub) sub.textContent = this.money(cart.items_subtotal_price);
    this.updateOrderSummary(cart);
    // lines
    if (this.linesEl) {
      this.linesEl.innerHTML = cart.items.map(this.lineHTML.bind(this)).join('');
    }
    // ship bar
    this.updateShipBar(cart);
    // The reward block is server-rendered and swapped in by the mutation that
    // produced this cart; the bar just re-reads its data attributes.
    // header bubble (native icon count)
    this.updateHeaderCount(cart.item_count);
    // build upsell once we know the cart
    this.buildUpsellOnce();
    // refresh carousel affordances (dots/chevron) for whatever cards exist now
    this.updateCarousel();
    // mobile sticky-stack scroll cue (shadow above the pinned stack)
    this.updateBagScrollState();
  };

  /* Mobile sticky stack: show an upward shadow on the pinned stack while
     the item list has unscrolled content (drops at bottom — affordance). */
  GSCart.prototype.updateBagScrollState = function () {
    var el = this.bagEl || (this.bagEl = $('.gs-cart-bag', this.panel));
    if (!el) return;
    var overflow = el.scrollHeight - el.clientHeight > 4;
    var atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
    this.panel.classList.toggle('gs-cart--overflow', overflow && !atBottom);
  };

  /* Mobile ORDER-SUMMARY bottom-sheet: collapsed by default, chevron expands
     it to reveal the full summary + upsell + reward ladder (desktop ignores this —
     the summary is always shown there). */
  GSCart.prototype.collapseSheet = function () {
    this.panel.classList.remove('gs-sheet-open');
    var t = $('[data-gs-sheet-toggle]', this.panel);
    if (t) t.setAttribute('aria-expanded', 'false');
  };
  /* Mobile/tablet: open the sheet on every drawer open. Collapsed, the upsell
     carousel and the reward block are both below the fold, which reads as "there
     is no upsell". The shopper can still collapse it with the chevron. */
  GSCart.prototype.expandSheet = function () {
    // Desktop has no bottom sheet — the summary column is always fully open, so
    // the class is not just redundant there, it used to drag mobile's 58vh cap
    // in with it.
    if (window.matchMedia && window.matchMedia('(min-width: 1025px)').matches) return;
    this.panel.classList.add('gs-sheet-open');
    var t = $('[data-gs-sheet-toggle]', this.panel);
    if (t) t.setAttribute('aria-expanded', 'true');
    var self = this;
    setTimeout(function () { self.updateCarousel(); }, 80);
  };
  GSCart.prototype.toggleSheet = function () {
    var open = this.panel.classList.toggle('gs-sheet-open');
    var t = $('[data-gs-sheet-toggle]', this.panel);
    if (t) t.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) { var self = this; setTimeout(function () { self.updateCarousel(); }, 80); }
  };

  GSCart.prototype.updateOrderSummary = function (cart) {
    var estShip = $('[data-gs-est-ship]', this.panel);
    if (estShip) {
      var th = this.shipThresholdCents();
      estShip.textContent = (th && cart.total_price >= th) ? 'FREE' : 'Calculated at checkout';
    }
    /* Order-level discounts (the cart reward ladder, WELCOME25) are allocated
       back onto the lines, so the line prices already show them while
       items_subtotal_price does not. Show that gap as its own row and make the
       estimated total the post-discount total_price, so the summary agrees with
       the lines and with checkout. The row hides when nothing is applied.
       (2026-09-13; the Liquid first paint in gs-cart-drawer / gs-cart-page
       renders the same row.) */
    var discount = Math.max(0, (cart.items_subtotal_price || 0) - (cart.total_price || 0));
    var row = $('[data-gs-discount-row]', this.panel);
    if (row) {
      row.hidden = discount <= 0;
      var codes = (cart.cart_level_discount_applications || []).map(function (d) { return d.title; }).filter(Boolean);
      var label = $('[data-gs-discount-label]', row);
      if (label) label.textContent = codes.length ? 'Discount (' + codes.join(', ') + ')' : 'Discount';
      var val = $('[data-gs-discount]', row);
      if (val) val.textContent = '-' + this.money(discount);
    }
    var total = this.money(cart.total_price);
    // Two totals share the hook: the summary row + the mobile sheet handle.
    $all('[data-gs-est-total]', this.panel).forEach(function (el) { el.textContent = total; });
  };

  /* ---- "Complete the look" carousel (mobile 1-up / desktop-left multi-up) ---- */
  GSCart.prototype.updateCarousel = function () {
    var track = this.upsellList;
    if (!track) return;
    var next = $('[data-gs-upsell-next]', this.panel);
    var prev = $('[data-gs-upsell-prev]', this.panel);
    var dotsWrap = $('[data-gs-upsell-dots]', this.panel);
    var cards = $all('.gs-up-card:not(.gs-up-card--more)', track);
    var multi = cards.length > 1;
    // A long carousel would spill a useless row of dots, so past DOT_LIMIT the
    // chevron alone carries the navigation.
    var DOT_LIMIT = 8;
    var showDots = multi && cards.length <= DOT_LIMIT;
    if (dotsWrap) {
      dotsWrap.innerHTML = showDots ? cards.map(function (_, i) {
        return '<button type="button" class="gs-upsell-dot' + (i === 0 ? ' is-active' : '') + '" aria-label="Show item ' + (i + 1) + '"></button>';
      }).join('') : '';
      dotsWrap.style.display = showDots ? '' : 'none';
    }
    if (next) next.style.display = multi ? '' : 'none';
    if (prev) prev.style.display = multi ? '' : 'none';
    this.updateCarouselState();
  };
  GSCart.prototype.updateCarouselState = function () {
    var track = this.upsellList;
    if (!track) return;
    var next = $('[data-gs-upsell-next]', this.panel);
    var prev = $('[data-gs-upsell-prev]', this.panel);
    var dotsWrap = $('[data-gs-upsell-dots]', this.panel);
    var cards = $all('.gs-up-card:not(.gs-up-card--more)', track);
    var atStart = track.scrollLeft <= 8;
    var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
    if (next) next.classList.toggle('is-end', atEnd);
    if (prev) prev.classList.toggle('is-start', atStart);
    if (dotsWrap && cards.length) {
      var center = track.scrollLeft + track.clientWidth / 2;
      var idx = 0, best = Infinity;
      cards.forEach(function (c, i) { var cc = c.offsetLeft + c.offsetWidth / 2; var d = Math.abs(cc - center); if (d < best) { best = d; idx = i; } });
      $all('.gs-upsell-dot', dotsWrap).forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
    }
  };

  GSCart.prototype.lineHTML = function (item) {
    // Material/variant line from options_with_values (skip Default Title).
    var optParts = [];
    (item.options_with_values || []).forEach(function (ov) {
      if (ov && ov.value && ov.value !== 'Default Title') optParts.push(ov.value);
    });
    var variant = optParts.length ? '<p class="gs-line__variant">' + escapeHtml(optParts.join(' / ')) + '</p>' : '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      variant += '<p class="gs-line__variant">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + '</p>';
    }
    var img = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2') : '';
    var media = img
      ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(item.product_title || item.title) + '" width="120" height="120" loading="lazy">'
      : '';
    var priceHtml = '';
    if (item.original_line_price > item.final_line_price) priceHtml += '<s>' + this.money(item.original_line_price) + '</s>';
    priceHtml += this.money(item.final_line_price);
    if (item.quantity > 1) priceHtml += '<span class="gs-line__unit">' + this.money(item.final_price) + ' each</span>';
    var title = escapeHtml((item.product_title || item.title || '').toUpperCase());

    return '' +
      '<li class="gs-line-row" data-gs-item>' +
        '<div class="gs-line" data-gs-key="' + escapeHtml(item.key) + '">' +
          '<a class="gs-line__media" href="' + escapeHtml(item.url) + '" tabindex="-1" aria-hidden="true">' + media + '</a>' +
          '<div class="gs-line__info">' +
            '<a class="gs-line__title" href="' + escapeHtml(item.url) + '">' + title + '</a>' +
            '<p class="gs-line__price">' + priceHtml + '</p>' +
            variant +
            '<p class="gs-line__stock">In stock</p>' +
            '<div class="gs-line__ctrls">' +
              '<div class="gs-stepper" role="group" aria-label="Quantity">' +
                '<button type="button" class="gs-stepper__btn" data-gs-qty-down aria-label="Decrease quantity">−</button>' +
                '<span class="gs-stepper__val" data-gs-qty aria-live="polite">' + item.quantity + '</span>' +
                '<button type="button" class="gs-stepper__btn" data-gs-qty-up aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button type="button" class="gs-line__remove" data-gs-remove aria-label="Remove item">Remove</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</li>';
  };

  /* ---- free-ship bar + badge ---- */
  GSCart.prototype.shipThresholdCents = function () {
    var country = this.panel.getAttribute('data-gs-country') || this.cfg.country;
    var dollars = country === 'CA' ? this.cfg.freeShipCA : this.cfg.freeShipIntl;
    return Number(dollars) * 100;
  };
  /* The bar tracks FREE SHIPPING only (Sept 11 2026, Ali's call). It used to
     re-target the next reward tier once shipping was unlocked (brief v2
     section 5, "one sequential bar"), but the reward block in the summary
     column already carries its own "ADD [amount] MORE … [reward] DISCOUNT"
     chase line, so the bar repeating it was noise. The reward ladder never
     touches this bar again — it reads nothing from rewardData().
     Free shipping is measured on the whole cart total, which is what Shopify
     ships on; the threshold is location-aware ($300 CA / $450 international,
     from the section settings). Hidden when the active threshold is 0. */
  GSCart.prototype.updateShipBar = function (cart) {
    var bar = $('[data-gs-shipbar]', this.panel);
    var fill = $('[data-gs-shipbar-fill]', this.panel);
    var text = $('[data-gs-shipbar-text]', this.panel);
    if (!fill || !text) return;

    var shipTh = this.shipThresholdCents();
    if (!shipTh) { if (bar) bar.style.display = 'none'; return; }
    if (bar) bar.style.display = '';

    var total = cart ? cart.total_price : 0;
    var pct, msg, unlocked;
    if (total < shipTh) {
      pct = (total / shipTh) * 100;
      msg = 'ADD ' + this.money(shipTh - total) + ' MORE FOR FREE SHIPPING';
      unlocked = false;
    } else {
      pct = 100;
      msg = "YOU'VE UNLOCKED FREE SHIPPING ✓";
      unlocked = true;
    }

    fill.style.width = (Math.round(Math.max(0, Math.min(100, pct)) * 100) / 100) + '%';
    text.textContent = msg;
    text.classList.toggle('is-unlocked', unlocked);
  };
  GSCart.prototype.updateShipBadge = function () {
    var el = $('[data-gs-ship-label]', this.panel);
    if (!el) return;
    var country = this.panel.getAttribute('data-gs-country') || this.cfg.country;
    var tpl = country === 'CA' ? this.cfg.shipLabelCA : this.cfg.shipLabelIntl;
    var amount = this.money.clean(this.shipThresholdCents());
    el.textContent = (tpl || 'FREE SHIPPING OVER [amount]').replace('[amount]', amount).toUpperCase();
  };

  /* ---- header cart-count bubble ---- */
  GSCart.prototype.updateHeaderCount = function (count) {
    var bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (bubble) bubble.textContent = count;
    $all('[data-cart-count]').forEach(function (el) { el.textContent = count; });
  };
  GSCart.prototype.replaceBubbleSection = function (html) {
    if (!html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var next = parsed.querySelector('.shopify-section') || parsed.body;
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && next) bubble.innerHTML = next.innerHTML;
  };

  /* ---- qty / remove ---- */
  GSCart.prototype.change = function (key, quantity, row) {
    var self = this;
    if (row) row.classList.add('is-busy');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity,
        sections: ['cart-icon-bubble', this.cfg.rewardSectionId].filter(Boolean),
        sections_url: window.location.pathname })
    }).then(function (res) {
      if (!res.ok) throw new Error('change-failed');
      return res.json();
    }).then(function (cart) {
      self.cart = cart;
      if (cart.sections) self.replaceBubbleSection(cart.sections['cart-icon-bubble']);
      self.render(cart);
      // Re-render of the reward block rides along with the mutation above.
      if (!(cart.sections && self.swapRewardBlock(cart.sections[self.cfg.rewardSectionId]))) self.fetchRewardSection();
      self.publishUpdate();
      return cart;
    }).catch(function () { if (row) row.classList.remove('is-busy'); });
  };

  /* ---- add to cart ---- */
  GSCart.prototype.addVariant = function (variantId, qty) {
    var self = this;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty || 1 }],
        sections: ['cart-icon-bubble', this.cfg.rewardSectionId].filter(Boolean),
        sections_url: window.location.pathname })
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (j) { throw j; });
      return res.json();
    }).then(function (data) {
      if (data.sections) {
        self.replaceBubbleSection(data.sections['cart-icon-bubble']);
        self.__pendingRewardSection = data.sections[self.cfg.rewardSectionId];
      }
      return data;
    });
  };
  GSCart.prototype.publishUpdate = function () {
    gsPublish(PSE('cartUpdate'), { source: 'gs-cart', cartData: this.cart });
  };

  GSCart.prototype.onUpsellAdd = function (btn) {
    var self = this;
    var id = btn.getAttribute('data-variant-id');
    if (!id || btn.disabled) return;
    var errEl = $('[data-gs-up-error]', this.panel);
    if (errEl) errEl.hidden = true;
    var original = btn.textContent;
    btn.disabled = true;
    this.addVariant(id, 1).then(function () {
      btn.classList.add('is-added');
      btn.textContent = 'ADDED ✓';
      return self.refresh();
    }).then(function () {
      if (!self.swapRewardBlock(self.__pendingRewardSection)) self.fetchRewardSection();
      self.__pendingRewardSection = null;
      // Rebuild so the item just added leaves the carousel. keep() already
      // excludes anything in the cart, but the build short-circuits while the
      // anchor is unchanged — so clear the anchor to force it.
      self.upsellAnchorKey = null;
      self.buildUpsellOnce();
      self.publishUpdate();
    }).catch(function (err) {
      // Surface Shopify's own reason ("Only 1 available", "maximum quantity")
      // rather than a generic failure the shopper cannot act on.
      var msg = (err && (err.description || err.message)) || "Couldn't add that. Try again.";
      if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
      btn.textContent = original;
    }).then(function () {
      setTimeout(function () {
        btn.disabled = false;
        if (btn.classList.contains('is-added')) { btn.classList.remove('is-added'); btn.textContent = original; }
      }, 1600);
    });
  };

  /* ============================================================
     Reward ladder — brief v2.

     The block is rendered by sections/gs-cart-drawer.liquid, which resolves
     the eligible subtotal and the tier server-side from live product objects.
     Nothing here re-derives either: on a cart mutation we take the freshly
     rendered section (piggybacked on the add/change request that was already
     going out, so no extra round trip) and swap the block in whole.

     JS keeps only what the server cannot do: copying the code and applying
     it. (The progress bar is free-shipping only since Sept 2026 — see
     updateShipBar.)
     ============================================================ */
  GSCart.prototype.rewardEl = function () { return $('[data-gs-promo]', this.panel); };

  GSCart.prototype.rewardData = function () {
    var el = this.rewardEl();
    if (!el) return null;
    var n = function (a) { return Number(el.getAttribute(a)) || 0; };
    return {
      state: el.getAttribute('data-gs-reward-state') || 'none',
      eligible: n('data-gs-eligible-cents'),
      gap: n('data-gs-reward-gap'),
      nextThreshold: n('data-gs-reward-next-threshold'),
      nextReward: n('data-gs-reward-next-reward')
    };
  };

  /* Swap in a server-rendered reward block from a Section Rendering payload. */
  GSCart.prototype.swapRewardBlock = function (sectionHtml) {
    if (!sectionHtml) return false;
    var cur = this.rewardEl();
    if (!cur) return false;
    var next;
    try {
      next = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[data-gs-promo]');
    } catch (e) { return false; }
    if (!next) return false;
    cur.parentNode.replaceChild(next, cur);
    this.afterRewardRender();
    return true;
  };

  /* Fallback when a mutation did not carry the section with it. */
  GSCart.prototype.fetchRewardSection = function () {
    var self = this;
    var id = this.cfg.rewardSectionId;
    if (!id) return Promise.resolve(false);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + '?sections=' + encodeURIComponent(id), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return d ? self.swapRewardBlock(d[id]) : false; })
      .catch(function () { return false; });
  };

  /* Apply the revealed code once, and only when it actually changes. Dropping
     below a rung deliberately leaves the old code on the cart: Shopify's own
     minimum-purchase rule refuses it at checkout, and there is no AJAX route
     to clear a discount. */
  GSCart.prototype.afterRewardRender = function () {
    var codeEl = $('[data-gs-code]', this.panel);
    var code = codeEl ? codeEl.textContent.trim() : '';
    if (code && code !== this.rwAppliedCode) {
      this.rwAppliedCode = code;
      this.applyDiscount(code);
    } else if (!code) {
      this.rwAppliedCode = null;
    }
    this.updateShipBar(this.cart);
    // A crossed rung changes the gap, so the band the carousel should lead with
    // moves too. Re-order what is already there rather than refetching.
    var track = this.upsellList;
    if (track && track.querySelector('.gs-up-card')) {
      var cards = $all('.gs-up-card:not(.gs-up-card--more)', track);
      var withPrice = cards.map(function (el) {
        return { el: el, price: Number(el.getAttribute('data-gs-price')) || 0 };
      });
      var rw = this.rewardData();
      if (rw && rw.gap > 0) {
        var hi = rw.gap * 1.4, band = [], rest = [];
        withPrice.forEach(function (x) { (x.price >= rw.gap && x.price <= hi ? band : rest).push(x); });
        if (band.length) {
          band.sort(function (a, b) { return a.price - b.price; });
          var more = track.querySelector('.gs-up-card--more');
          band.concat(rest).forEach(function (x) { track.insertBefore(x.el, more || null); });
          track.scrollLeft = 0;
        }
      }
    }
  };

  /* ---- discount ---- */
  GSCart.prototype.applyDiscount = function (code) {
    if (!code) return;
    try { fetch('/discount/' + encodeURIComponent(code)).catch(function () {}); } catch (e) {}
  };
  /* ---- wiring ---- */
  GSCart.prototype.wire = function () {
    var self = this;

    // qty / remove / upsell-add via delegation on the panel
    this.panel.addEventListener('click', function (e) {
      var sheet = e.target.closest('[data-gs-sheet-toggle]');
      if (sheet) { e.preventDefault(); self.toggleSheet(); return; }

      var up = e.target.closest('.gs-up-add');
      if (up) { e.preventDefault(); self.onUpsellAdd(up); return; }

      var cont = e.target.closest('[data-gs-continue]');
      if (cont) { e.preventDefault(); self.core.close(); return; }

      var copy = e.target.closest('[data-gs-copy]');
      if (copy) { e.preventDefault(); self.copyCode(copy); return; }

      var row = e.target.closest('.gs-line');
      if (!row) return;
      var key = row.getAttribute('data-gs-key');
      var qtyEl = $('[data-gs-qty]', row);
      var qty = qtyEl ? parseInt(qtyEl.textContent, 10) || 1 : 1;
      if (e.target.closest('[data-gs-qty-up]')) { e.preventDefault(); self.change(key, qty + 1, row); }
      else if (e.target.closest('[data-gs-qty-down]')) { e.preventDefault(); self.change(key, Math.max(0, qty - 1), row); }
      else if (e.target.closest('[data-gs-remove]')) { e.preventDefault(); self.change(key, 0, row); }
    });

    // "Complete the look" carousel: chevrons scroll by one card (mobile: next only;
    // desktop-left: prev + next); dots jump.
    function cardStep() {
      var track = self.upsellList; if (!track) return 0;
      var card = track.querySelector('.gs-up-card');
      return card ? card.getBoundingClientRect().width + 14 : track.clientWidth * 0.86;
    }
    var nextBtn = $('[data-gs-upsell-next]', this.panel);
    if (nextBtn) nextBtn.addEventListener('click', function () {
      if (self.upsellList) smoothScrollTo(self.upsellList, self.upsellList.scrollLeft + cardStep());
    });
    var prevBtn = $('[data-gs-upsell-prev]', this.panel);
    if (prevBtn) prevBtn.addEventListener('click', function () {
      if (self.upsellList) smoothScrollTo(self.upsellList, self.upsellList.scrollLeft - cardStep());
    });
    if (this.upsellList) this.upsellList.addEventListener('scroll', function () { self.updateCarouselState(); }, { passive: true });
    var bag = $('.gs-cart-bag', this.panel);
    if (bag) bag.addEventListener('scroll', function () { self.updateBagScrollState(); }, { passive: true });
    var dotsWrap = $('[data-gs-upsell-dots]', this.panel);
    if (dotsWrap) dotsWrap.addEventListener('click', function (e) {
      var dot = e.target.closest('.gs-upsell-dot'); if (!dot || !self.upsellList) return;
      var idx = Array.prototype.indexOf.call(dotsWrap.children, dot);
      var card = self.upsellList.querySelectorAll('.gs-up-card')[idx];
      if (card) smoothScrollTo(self.upsellList, card.offsetLeft - (self.upsellList.clientWidth - card.offsetWidth) / 2);
    });
    this.updateCarousel();
  };
  GSCart.prototype.copyCode = function (copyBtn) {
    var codeEl = $('[data-gs-code]', this.panel);
    if (!codeEl) return;
    var code = codeEl.textContent.trim();
    if (!code) return;
    var doneLabel = 'COPIED', idleLabel = copyBtn.textContent.trim() || 'COPY';
    var ok = function () { copyBtn.textContent = doneLabel; setTimeout(function () { copyBtn.textContent = idleLabel; }, 1500); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(ok, ok);
    else {
      var t = document.createElement('textarea');
      t.value = code; document.body.appendChild(t); t.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(t); ok();
    }
  };

  /* The same seven rules the `credit-eligible` collection uses, applied to an
     upsell candidate. Suggesting an item that cannot count toward the reward
     would be doubly wrong: it cannot close the gap, and Nivoda stock carries a
     standing no-discount-messaging rule. Candidate payloads carry type, tags
     and title, so unlike a cart line these can be judged client-side. */
  function isCreditEligibleCandidate(p) {
    if (!p) return false;
    var t = String(p.type || p.product_type || '').toLowerCase().trim();
    if (t === 'setting' || t === 'diamond' || t === 'gemstone' || t === 'loose diamond') return false;
    var tags = p.tags;
    if (typeof tags === 'string') tags = tags.split(',');
    var tg = '|' + (tags || []).join('|').toLowerCase() + '|';
    if (tg.indexOf('|builder-only|') !== -1 || tg.indexOf('|no-reward|') !== -1) return false;
    if (String(p.title || '').toLowerCase().indexOf('repair') !== -1) return false;
    return true;
  }

  /* ---- upsell: metal tier + care-product rules (2026-09-13) ----
     With an 18K chain in the cart the carousel was offering $20 cleaners and
     $27 silver chains. Two rules now sit on top of the credit-eligibility gate:
       1. never a care product (cleaner / cleaning / cloth / polishing; bare
          "polish" is NOT used because "High Polish" and "Matte Polish" are
          finishes on real jewellery);
       2. same METAL TIER as the anchor line: gold (10k/14k/18k/22k/24k or the
          word gold, unless plated / vermeil / filled / gold-tone), silver
          (sterling / 925 / silver), or other (platinum, steel, unknown).
     The tier is read from the words in title + type + tags because that is all
     /cart.js and the candidate payloads carry. If fewer than 2 cards survive
     the tier rule the whole pipeline runs once more with the tier rule off
     (rule 1 stays), so the carousel is never left empty. */
  var CARE_RE = /\bcleaner\b|\bcleaning\b|\bcloth\b|\bpolishing\b/i;
  function metalTier(text) {
    text = String(text || '').toLowerCase();
    var gold = /\b(10|14|18|22|24)\s?k(t|arat)?\b|\bgold\b/.test(text) && !/plated|vermeil|filled|gold[- ]?tone/.test(text);
    if (gold) return 'gold';
    if (/sterling|\b925\b|\bsilver\b/.test(text)) return 'silver';
    return 'other';
  }
  function productText(p) {
    var tags = Array.isArray(p.tags) ? p.tags.join(' ') : String(p.tags || '');
    return (p.title || '') + ' ' + (p.type || p.product_type || '') + ' ' + tags;
  }

  /* ---- upsell: similar mode (anchored to cart) ---- */
  function splitList(str) { return String(str || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean); }
  function pieceRe(kw) { var r = kw.replace(/s$/i, ''); return new RegExp('\\b' + escapeRe(r) + 's?\\b', 'i'); }
  function handleFromUrl(u) { var m = String(u || '').match(/\/products\/([^/?#]+)/); return m ? m[1] : null; }

  GSCart.prototype.upsellCardHTML = function (d) {
    var price = this.money(d.price);
    if (d.compareAt && d.compareAt > d.price) price += '<s>' + this.money(d.compareAt) + '</s>';
    return '<article class="gs-up-card" data-variant-id="' + d.variantId +
      '" data-gs-price="' + (Number(d.price) || 0) + '">' +
      '<img src="' + escapeHtml(d.image) + '" alt="' + escapeHtml(d.title) + '" width="72" height="72" loading="lazy">' +
      '<div class="gs-up-info"><p class="gs-up-title">' + escapeHtml(d.title.toUpperCase()) + '</p>' +
      '<p class="gs-up-price">' + price + '</p></div>' +
      cta(d) + '</article>';
    function cta(d) {
      /* A ring setting on its own is a mounting with no diamond. Never give it
         an add control from a card — send it to the builder instead. */
      var niv = window.GSJNivoda || null;
      if (niv && niv.isSetting({ id: d.productId, type: d.type, tags: d.tags, variants: [{ id: d.variantId, available: true }] })) {
        var href = niv.builderUrl({ id: d.productId, type: d.type, tags: d.tags, variants: [{ id: d.variantId, available: true }] });
        return '<a class="gs-up-add" href="' + escapeHtml(href) +
          '" aria-label="Customize ' + escapeHtml(d.title) + ' in the ring builder">CUSTOMIZE<span class="gs-up-add__more"> +</span></a>';
      }
      return '<button type="button" class="gs-up-add" data-variant-id="' + d.variantId +
        '" aria-label="Add ' + escapeHtml(d.title) + ' to your order">ADD<span class="gs-up-add__more"> TO ORDER</span></button>';
    }
  };
  GSCart.prototype.resolveVariant = function (handle) {
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return null;
        var v = null, i;
        for (i = 0; i < p.variants.length; i++) { if (p.variants[i].available) { v = p.variants[i]; break; } }
        if (!v) return null; // nothing purchasable — do not offer it
        return { variantId: v.id, title: p.title, price: v.price, compareAt: v.compare_at_price,
          image: (p.featured_image || (p.images && p.images[0]) || ''), handle: handle,
          productId: p.id, type: p.type || p.product_type, tags: p.tags };
      }).catch(function () { return null; });
  };
  GSCart.prototype.predictiveSearch = function (material) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(material) +
      '&resources[type]=product&resources[limit]=10' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,tag,variants.title';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { try { return data.resources.results.products || []; } catch (e) { return []; } })
      .catch(function () { return []; });
  };
  GSCart.prototype.recommendations = function (productId, limit) {
    limit = Math.min(10, Math.max(1, Number(limit) || 10)); // endpoint caps at 10
    return fetch('/recommendations/products.json?product_id=' + productId + '&intent=related&limit=' + limit, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return (d && d.products) || []; }).catch(function () { return []; });
  };

  /* Add-on lines (Damage Protection etc.) are never the upsell anchor and
     never suggested as products. */
  function isAddonLine(it) {
    if (it && it.properties && it.properties._gs_for_key) return true;
    if (it && it.product_type === 'Warranty') return true;
    return typeof (it && it.handle) === 'string' && it.handle.indexOf('damage-protection') === 0;
  }

  /* Build/refresh the "Complete the look" cards.
     REMEDIATION 2026-07-11 (upsell-empty bug): the old build ran ONCE per page
     and silently bailed when the anchor title had no material keyword (custom
     pieces like "Completed Ring…"), leaving the server fallback — which is
     EMPTY when no fallback collection / curated products are configured.
     Now: (a) idempotent — called after EVERY cart render (the render path
     only rewrites [data-gs-cart-lines], never this carousel's ancestors, so
     re-init-after-render is the chosen strategy per the brief) and rebuilds
     when the anchor product changes; (b) a fallback chain guarantees cards
     for ANY cart: material-similar search → Shopify recommendations →
     configured fallback collection → newest products. */
  GSCart.prototype.buildUpsellOnce = function () {
    var self = this;
    if (!this.upsellList) return;
    if (this.upsellList.getAttribute('data-gs-upsell-mode') !== 'similar') return;
    if (!this.cart) return;

    var items = this.cart.items.filter(function (it) { return !isAddonLine(it); });
    if (!items.length) return; // empty (or warranty-only) cart — drawer shows empty state

    // anchor = highest-priced real line (ties go to the last-added)
    var anchor = items[items.length - 1];
    items.forEach(function (it) { if (it.final_line_price > anchor.final_line_price) anchor = it; });

    var anchorKey = String(anchor.product_id || anchor.handle);
    if (this.upsellAnchorKey === anchorKey && this.upsellList.querySelector('.gs-up-card')) return; // fresh
    if (this.upsellPending === anchorKey) return; // build already in flight

    // How many cards we want NOW. Read before the cache check: a cache written
    // when the setting was lower must not keep serving that smaller list.
    var count = Number(this.cfg.upsellCount) || 3;
    // v4 prefix (2026-09-13): abandons every v3 cache built before the
    // metal-tier and care-product rules, which would otherwise keep serving
    // cleaners for the rest of the session. builtFor makes the check exact.
    var cacheKey = 'gsUpsell4:' + anchorKey;
    // The cache is keyed on the anchor, not on cart contents, so a product added
    // since it was written would still be recommended back to the shopper. Filter
    // it against the cart on every read, and rebuild if that empties it.
    var inCart = {};
    this.cart.items.forEach(function (it) {
      if (it.handle) inCart['h:' + it.handle] = 1;
      if (it.product_id) inCart['p:' + it.product_id] = 1;
    });
    try {
      var cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      // Reuse only if it was built for at least as many cards as we want now.
      // (>= rather than == so a small catalogue that could only yield 12 for a
      // target of 17 still counts as a hit instead of rebuilding on every open.)
      if (cached && cached.cards && cached.cards.length && Number(cached.builtFor) >= count) {
        var fresh = cached.cards.filter(function (c) {
          return !inCart['h:' + c.handle] && !inCart['p:' + c.productId];
        });
        if (fresh.length) { this.upsellAnchorKey = anchorKey; this.renderUpsell(fresh); return; }
      }
    } catch (e) {}
    this.upsellPending = anchorKey;

    var materials = splitList(this.cfg.similarMaterials).sort(function (a, b) { return b.length - a.length; });
    var pieces = splitList(this.cfg.similarPieceTypes).sort(function (a, b) { return b.length - a.length; });
    var haystack = ((anchor.product_title || '') + ' ' + (anchor.product_type || '')).toLowerCase();

    var matched = [];
    materials.forEach(function (m) {
      if (matched.length >= 2) return;
      if (haystack.indexOf(m.toLowerCase()) !== -1) {
        var dup = matched.some(function (x) { return x.toLowerCase().indexOf(m.toLowerCase()) !== -1; });
        if (!dup) matched.push(m);
      }
    });

    var anchorPiece = null;
    var pieceHay = (anchor.product_type || '') + ' ' + (anchor.product_title || '');
    for (var i = 0; i < pieces.length; i++) { if (pieceRe(pieces[i]).test(pieceHay)) { anchorPiece = pieces[i]; break; } }
    var dropRe = anchorPiece ? pieceRe(anchorPiece) : null;

    // Metal tier of the anchor line (see metalTier above). /cart.js has no tags,
    // so this reads the line's title + type only.
    var anchorTier = metalTier((anchor.product_title || '') + ' ' + (anchor.product_type || ''));

    // exclusion sets: everything already in the cart (incl. add-on lines)
    var cartHandles = {}, cartProductIds = {};
    this.cart.items.forEach(function (it) { if (it.handle) cartHandles[it.handle] = 1; if (it.product_id) cartProductIds[it.product_id] = 1; });

    /* One full pass of the four-stage pipeline (material search -> Shopify
       recommendations -> fallback collection -> newest). tierStrict=true applies
       the metal-tier rule; false is the fallback pass (care products still drop).
       Each pass keeps its own `seen` set so the fallback can re-consider
       products the strict pass turned away. Resolves to the card list. */
    function collect(tierStrict) {
      function keep(h) {
        var handle = h.handle || handleFromUrl(h.url);
        if (!handle) return false;
        if (handle.indexOf('damage-protection') === 0) return false; // never suggest the warranty
        if ((h.product_type || h.type || '') === 'Warranty') return false;
        if (cartHandles[handle]) return false;
        if (h.id && cartProductIds[h.id]) return false;
        var text = (h.product_type || h.type || '') + ' ' + (h.title || '');
        if (dropRe && dropRe.test(text)) return false; // force a different piece
        if (!isCreditEligibleCandidate(h)) return false; // must be able to close the gap
        if (CARE_RE.test(text)) return false; // rule 1: never a care product
        if (tierStrict && metalTier(productText(h)) !== anchorTier) return false; // rule 2: same metal tier
        return true;
      }

      var seen = {};
      items.forEach(function (it) { if (it.handle) seen[it.handle] = 1; });

      // products.json / collection products → card shape (prices are dollar strings there)
      function productToCard(p) {
        if (!keep(p)) return null;
        var v = null;
        for (var j = 0; j < (p.variants || []).length; j++) { if (p.variants[j].available) { v = p.variants[j]; break; } }
        // No purchasable variant => no card. Falling back to variants[0] here used
        // to produce a card whose ADD could only ever fail.
        if (!v) return null;
        return {
          variantId: v.id, title: p.title,
          /* products.json gives dollar strings ("695.00"); the recommendations
             payload gives integer cents. Detect rather than assume. */
          price: typeof v.price === 'number' ? v.price : Math.round(parseFloat(v.price) * 100),
          compareAt: !v.compare_at_price ? null
            : (typeof v.compare_at_price === 'number' ? v.compare_at_price : Math.round(parseFloat(v.compare_at_price) * 100)),
          image: (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '',
          handle: p.handle,
          /* Nivoda mountings may legitimately surface as an upsell candidate, but
             they must never carry an ADD control — see upsellCardHTML. */
          productId: p.id, type: p.type || p.product_type, tags: p.tags
        };
      }
      function fillFromProducts(out, products) {
        (products || []).forEach(function (p) {
          if (out.length >= count) return;
          if (seen[p.handle]) return;
          var card = productToCard(p);
          if (card) { seen[p.handle] = 1; out.push(card); }
        });
        return out;
      }

      // Stage 1 — material-similar predictive search (skipped when no material matches)
      var stage1 = !matched.length ? Promise.resolve([]) :
        Promise.all(matched.map(function (m) { return self.predictiveSearch(m); })).then(function (groups) {
          var hits = [];
          groups.forEach(function (g) {
            g.filter(keep).forEach(function (h) { var k = h.handle || handleFromUrl(h.url); if (k && !seen[k]) { seen[k] = 1; hits.push(h); } });
          });
          // Each of these is its own /products/<handle>.js request, so the fan-out is
          // capped; stages 2-4 return whole product objects and fill the rest for free.
          var RESOLVE_CAP = 10;
          var handles = hits.map(function (h) { return h.handle || handleFromUrl(h.url); })
            .filter(Boolean).slice(0, Math.min(count + 3, RESOLVE_CAP));
          return Promise.all(handles.map(function (h) { return self.resolveVariant(h); }))
            .then(function (cards) { return cards.filter(Boolean).slice(0, count); });
        });

      return stage1.then(function (out) {
        // Stage 2 — Shopify recommendations for the anchor product
        if (out.length >= count) return out;
        return self.recommendations(anchor.product_id, 10).then(function (recs) {
          var usable = recs.filter(keep).filter(function (r) { return r.handle && !seen[r.handle]; });
          // Free path: the payload already carries variants.
          var needFetch = [];
          usable.forEach(function (r) {
            if (out.length >= count) return;
            seen[r.handle] = 1;
            if (r.variants && r.variants.length) {
              var card = productToCard(r);
              if (card) { out.push(card); return; }
            }
            needFetch.push(r.handle);
          });
          if (out.length >= count || !needFetch.length) return out;
          return Promise.all(needFetch.slice(0, 10).map(function (h) { return self.resolveVariant(h); })).then(function (rc) {
            rc.filter(Boolean).forEach(function (c) { if (out.length < count) out.push(c); });
            return out;
          });
        }).catch(function () { return out; });
      }).then(function (out) {
        // Stage 3 — configured fallback collection
        if (out.length >= count || !self.cfg.fallbackCollection) return out;
        return fetch('/collections/' + self.cfg.fallbackCollection + '/products.json?limit=' + Math.min(250, Math.max(60, count * 5)), { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) { return fillFromProducts(out, d && d.products); })
          .catch(function () { return out; });
      }).then(function (out) {
        // Stage 4 — newest products. Tops up a short list, not just an empty one:
        // the eligibility gate can leave the earlier stages well under `count`.
        if (out.length >= count) return out;
        return fetch('/products.json?limit=' + Math.min(250, Math.max(60, count * 5)), { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) { return fillFromProducts(out, d && d.products); })
          .catch(function () { return out; });
      });
    }

    collect(true).then(function (cards) {
      if (cards && cards.length >= 2) return cards;
      // Fewer than 2 same-tier cards: run the pipeline again with the tier rule
      // off. Care products stay excluded on this pass too.
      return collect(false);
    }).then(function (cards) {
      self.upsellPending = null;
      if (cards && cards.length) {
        cards = self.orderByRewardGap(cards);
        self.upsellAnchorKey = anchorKey;
        self.renderUpsell(cards);
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ builtFor: count, cards: cards })); } catch (e) {}
      }
    }).catch(function () { self.upsellPending = null; });
  };
  /* The end-of-carousel card out to a full collection. Server-rendered by
     gs-upsell.liquid, but a JS rebuild rewrites the track's innerHTML, so it has
     to be re-appended here or it would only survive the no-JS path. */
  /* Brief v2 section 6: lead with items priced between the reward gap and
     gap x 1.4, ascending — the band most likely to hold a real impulse add that
     actually closes the gap. Everything else keeps its existing order behind
     them, so the carousel still runs to its full configured depth rather than
     collapsing to whatever happens to sit in the band. */
  GSCart.prototype.orderByRewardGap = function (cards) {
    var rw = this.rewardData();
    var gap = rw ? rw.gap : 0;
    if (!gap || gap <= 0) return cards;
    var hi = gap * 1.4;
    var band = [], rest = [];
    cards.forEach(function (c) {
      var pr = Number(c.price) || 0;
      if (pr >= gap && pr <= hi) band.push(c); else rest.push(c);
    });
    band.sort(function (a, b) { return (Number(a.price) || 0) - (Number(b.price) || 0); });
    return band.concat(rest);
  };

  GSCart.prototype.upsellMoreCardHTML = function () {
    var url = this.cfg.upsellMoreUrl;
    if (!url) return '';
    var label = (this.cfg.upsellMoreLabel || 'VIEW MORE').toUpperCase();
    return '<a class="gs-up-card gs-up-card--more" href="' + escapeHtml(url) + '" data-gs-upsell-more>' +
      '<span class="gs-up-more__label">' + escapeHtml(label) + '</span>' +
      '<svg class="gs-up-more__arrow" aria-hidden="true" viewBox="0 0 24 24" fill="none">' +
      '<path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></a>';
  };
  GSCart.prototype.renderUpsell = function (cards) {
    if (!this.upsellList || !cards || !cards.length) return;
    this.upsellList.innerHTML = cards.map(this.upsellCardHTML.bind(this)).join('') + this.upsellMoreCardHTML();
    this.updateCarousel();
  };

  /* ============================================================
     GSCartPage — the /cart page (page-mode reuse of GSCart).
     Same AJAX render / qty / remove / similar upsell /
     location-aware free-ship bar, but inline on the page (no PanelCore: no
     open/close, scrim, focus-trap or bottom-sheet). Scopes every GSCart query
     to the page root via this.panel = root.
     ============================================================ */
  function GSCartPage(root) {
    this.root = root;
    this.panel = root; // query scope for the shared GSCart methods
    var cfgEl = $('[data-gs-config]', root);
    try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { this.cfg = {}; }
    this.money = makeMoney(this.cfg);
    this.cart = null;
    this.linesEl = $('[data-gs-cart-lines]', root);
    this.upsellList = $('[data-gs-upsell-list]', root);
    // Stub "core" so shared methods that touch it stay safe (there is no panel).
    this.core = { useGsap: false, isOpen: true, open: function () {}, close: function () {} };
    this.wire();
    this.updateShipBadge();
    this.refresh(); // the page is always "open"
  }
  GSCartPage.prototype = Object.create(GSCart.prototype);
  GSCartPage.prototype.constructor = GSCartPage;
  // No drawer animations / bottom-sheet on the page.
  GSCartPage.prototype.scheduleAnim = function () {};
  GSCartPage.prototype.animateLines = function () {};
  GSCartPage.prototype.collapseSheet = function () {};
  GSCartPage.prototype.toggleSheet = function () {};
  // gs-warranty.js only wraps the DRAWER instance, so render Damage-Protection
  // rows here too (a real cart line must still show on the page).
  GSCartPage.prototype.lineHTML = function (item) {
    if (!isAddonLine(item)) return GSCart.prototype.lineHTML.call(this, item);
    var forTitle = (item.properties && item.properties.Protects) || '';
    return '' +
      '<li class="gs-line-row" data-gs-item>' +
        '<div class="gs-line gs-line--warranty" data-gs-key="' + escapeHtml(item.key) + '">' +
          '<span class="gs-line__media gs-line__media--wty" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" fill="none"><path d="M12 2.5 4.5 5.5v6c0 4.6 3.2 8.1 7.5 9.9 4.3-1.8 7.5-5.3 7.5-9.9v-6L12 2.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="m8.7 11.6 2.3 2.3 4.3-4.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</span>' +
          '<div class="gs-line__info">' +
            '<p class="gs-line__title">DAMAGE PROTECTION</p>' +
            (forTitle ? '<p class="gs-line__variant">For: ' + escapeHtml(forTitle) + '</p>' : '') +
            '<div class="gs-line__ctrls">' +
              '<button type="button" class="gs-line__remove" data-gs-remove aria-label="Remove Damage Protection">Remove</button>' +
            '</div>' +
          '</div>' +
          '<div class="gs-line__prices"><p class="gs-line__price">' + this.money(item.final_line_price) + '</p></div>' +
        '</div>' +
      '</li>';
  };

  /* ============================================================
     Global integration: cart-icon trigger + add-to-cart routing
     ============================================================ */
  function wireGlobalCart(cart) {
    // 1) Header cart icon → open the drawer (Dawn's own handler is gone: its
    //    <cart-drawer> element was removed in theme.liquid).
    function wireIcon() {
      var icon = document.getElementById('cart-icon-bubble');
      if (!icon || icon.__gsWired) return;
      icon.__gsWired = true;
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-haspopup', 'dialog');
      icon.setAttribute('aria-controls', 'gs-cart-panel');
      icon.addEventListener('click', function (e) { e.preventDefault(); cart.openCart(icon); });
      icon.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.code === 'Space' || e.key === ' ') { e.preventDefault(); cart.openCart(icon); }
      });
    }
    wireIcon();
    document.addEventListener('shopify:section:load', wireIcon);

    // 2) Intercept add-to-cart form submits (capture phase → preempts Dawn's
    //    product-form handler). Route through /cart/add.js then open the drawer.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      if (form.hasAttribute('data-gs-skip')) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      var fd = new FormData(form);
      fd.append('sections', ['cart-icon-bubble', cart.cfg.rewardSectionId].filter(Boolean).join(','));
      fd.append('sections_url', window.location.pathname);
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.setAttribute('aria-disabled', 'true');

      fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.status) { // add error (sold out, etc.) — let the page recover
            gsPublish(PSE('cartError'), { source: 'gs-cart', message: data.message });
            return;
          }
          if (data && data.sections) cart.replaceBubbleSection(data.sections['cart-icon-bubble']);
          cart.refresh().then(function () {
            if (!(data && data.sections && cart.swapRewardBlock(data.sections[cart.cfg.rewardSectionId]))) cart.fetchRewardSection();
            cart.publishUpdate();
          });
          if (cart.cfg.openCartOnAdd !== false) cart.openCart(submitBtn || null);
        })
        .catch(function () {})
        .then(function () { if (submitBtn) submitBtn.removeAttribute('aria-disabled'); });
    }, true); // capture

    // 3) Secondary: react to theme/app cart updates we didn't originate.
    var updateEvent = PSE('cartUpdate');
    if (updateEvent) {
      gsSubscribe(updateEvent, function (evt) {
        if (evt && evt.source === 'gs-cart') return;
        cart.refresh().then(function () { cart.fetchRewardSection(); });
        if (cart.cfg.openCartOnAdd !== false && !cart.core.isOpen) cart.openCart(null);
      });
    }
  }

  /* ============================================================
     init
     ============================================================ */
  function initAll() {
    var menuRoot = document.getElementById('gs-menu-root');
    if (menuRoot && !menuRoot.__gsInit) {
      menuRoot.__gsInit = true;
      document.documentElement.classList.add('gs-menu-enabled');
      window.GSMenuInstance = new GSMenu(menuRoot);
    }
    var cartRoot = document.getElementById('gs-cart-root');
    if (cartRoot && !cartRoot.__gsInit) {
      cartRoot.__gsInit = true;
      var cart = new GSCart(cartRoot);
      window.GSCartInstance = cart;
      wireGlobalCart(cart);
    }
    var cartPageRoot = document.getElementById('gs-cart-page-root');
    if (cartPageRoot && !cartPageRoot.__gsInit) {
      cartPageRoot.__gsInit = true;
      window.GSCartPageInstance = new GSCartPage(cartPageRoot);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // Theme editor: re-init a panel section if it re-renders.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && (e.target.querySelector('#gs-menu-root') || e.target.querySelector('#gs-cart-root'))) initAll();
  });
})();
